import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  Plus,
  RefreshCw,
  Search,
  X
} from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  CALENDAR_VIEWS,
  rangeTitle,
  type CalendarView
} from "../calendarModel";
import {
  DEFAULT_REMINDER_FILTERS,
  KIND_FILTER_LABEL,
  STATUS_FILTER_LABEL,
  activeFilterCount,
  type KindFilter,
  type ReminderFilters,
  type StatusFilter
} from "../remindersModel";

const ANYONE = "__anyone__";

export type TeamOption = { value: string; label: string };

/** The segment's order; Agenda stays reachable after Month. */
const SEGMENT_VIEWS: CalendarView[] = ["day", "week", "month", "agenda"];

type Props = {
  /** A muted name after the range ("Firm Calendar"). */
  label?: ReactNode;
  /** `segment` shows Day / Week / Month / Agenda inline; `menu` one dropdown. */
  viewControl?: "segment" | "menu";
  view: CalendarView;
  anchor: Date;
  onView: (view: CalendarView) => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  filters: ReminderFilters;
  onFilters: (next: ReminderFilters) => void;
  teamOptions: TeamOption[];
  onNew: () => void;
  /** Recompute automatic reminders; omitted outside a client's profile. */
  onRecompute?: () => void;
  isRecomputing?: boolean;
  /** Extra `FilterSection`s shown first in the filters popover (e.g. Source). */
  extraFilters?: ReactNode;
  /** How many of the extra filters differ from their defaults (badge count). */
  extraFilterCount?: number;
  /** Resets the extra filters alongside "Clear filters". */
  onClearExtraFilters?: () => void;
  className?: string;
};

export const FilterSection: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium text-fg-3">{label}</p>
    {children}
  </div>
);

const FiltersPopover: FC<
  Pick<
    Props,
    | "filters"
    | "onFilters"
    | "teamOptions"
    | "extraFilters"
    | "extraFilterCount"
    | "onClearExtraFilters"
  >
> = ({
  filters,
  onFilters,
  teamOptions,
  extraFilters,
  extraFilterCount = 0,
  onClearExtraFilters
}) => {
  const count = activeFilterCount(filters) + extraFilterCount;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Filters">
          <ListFilter />
          <span className="hidden sm:inline">Filters</span>
          {count > 0 ? (
            <span className="text-fg-4 tabular-nums">{count}</span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] space-y-3 p-3">
        <InputGroup className="h-8">
          <InputGroupAddon>
            <Search className="size-3.5" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder="Search title, notes, location"
            value={filters.search}
            onChange={(e) => onFilters({ ...filters, search: e.target.value })}
          />
        </InputGroup>
        {extraFilters}
        <FilterSection label="Type">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={filters.kind}
            onValueChange={(v) => {
              if (v) onFilters({ ...filters, kind: v as KindFilter });
            }}
            className="w-full"
          >
            {(Object.keys(KIND_FILTER_LABEL) as KindFilter[]).map((k) => (
              <ToggleGroupItem key={k} value={k} className="flex-1 text-xs">
                {k === "ALL" ? "All" : KIND_FILTER_LABEL[k]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </FilterSection>
        <FilterSection label="Status">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={filters.status}
            onValueChange={(v) => {
              if (v) onFilters({ ...filters, status: v as StatusFilter });
            }}
            className="w-full"
          >
            {(Object.keys(STATUS_FILTER_LABEL) as StatusFilter[]).map((k) => (
              <ToggleGroupItem key={k} value={k} className="flex-1 text-xs">
                {k === "ALL" ? "All" : k === "OPEN" ? "Open" : "Closed"}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </FilterSection>
        <FilterSection label="Assigned To">
          <Select
            value={filters.assignedTo || ANYONE}
            onValueChange={(v) =>
              onFilters({ ...filters, assignedTo: v === ANYONE ? "" : v })
            }
          >
            <SelectTrigger
              className="h-8 w-full text-xs"
              aria-label="Assigned to"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANYONE}>Anyone</SelectItem>
              {teamOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterSection>
        {count > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full text-xs"
            onClick={() => {
              onFilters(DEFAULT_REMINDER_FILTERS);
              onClearExtraFilters?.();
            }}
          >
            <X className="size-3.5" /> Clear Filters
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

/**
 * One 52px row: the range, its label, Today and the arrows on the left;
 * filters, the view control and New Event on the right.
 */
const ViewMenu: FC<Pick<Props, "view" | "onView">> = ({ view, onView }) => {
  const current =
    CALENDAR_VIEWS.find((v) => v.id === view) ?? CALENDAR_VIEWS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" aria-label={`View: ${current.label}`}>
          {current.label}
          <ChevronDown className="text-fg-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {CALENDAR_VIEWS.map((v) => (
          <DropdownMenuItem
            key={v.id}
            onSelect={() => onView(v.id)}
            className={cn(v.id === view && "bg-fill-2")}
          >
            {v.label}
            <DropdownMenuShortcut>{v.shortcut}</DropdownMenuShortcut>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ViewSegment: FC<Pick<Props, "view" | "onView">> = ({ view, onView }) => (
  <Tabs value={view} onValueChange={(v) => onView(v as CalendarView)}>
    <TabsList aria-label="View" data-testid="calendar-view-segment">
      {SEGMENT_VIEWS.map((id) => {
        const v = CALENDAR_VIEWS.find((c) => c.id === id);
        return v ? (
          <TabsTrigger key={id} value={id} title={`${v.label} (${v.shortcut})`}>
            {v.label}
          </TabsTrigger>
        ) : null;
      })}
    </TabsList>
  </Tabs>
);

export const CalendarToolbar: FC<Props> = ({
  label,
  viewControl = "menu",
  view,
  anchor,
  onView,
  onToday,
  onPrev,
  onNext,
  filters,
  onFilters,
  teamOptions,
  onNew,
  onRecompute,
  isRecomputing = false,
  extraFilters,
  extraFilterCount,
  onClearExtraFilters,
  className
}) => (
  <div
    className={cn(
      "flex min-h-13 shrink-0 flex-wrap items-center gap-2 border-b border-line-2 py-2 pr-4 pl-5",
      className
    )}
  >
    <h2 className="min-w-0 truncate text-lg font-medium text-fg">
      {rangeTitle(view, anchor)}
    </h2>
    {label ? (
      <span className="shrink-0 text-base text-fg-3">{label}</span>
    ) : null}
    <Button size="sm" onClick={onToday} className="ml-2">
      Today
    </Button>
    <div className="flex items-center">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Previous"
        onClick={onPrev}
      >
        <ChevronLeft />
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Next" onClick={onNext}>
        <ChevronRight />
      </Button>
    </div>

    <div className="ml-auto flex items-center gap-1.5">
      {onRecompute ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Recompute automatic reminders"
              disabled={isRecomputing}
              onClick={onRecompute}
            >
              <RefreshCw className={cn(isRecomputing && "animate-spin")} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Recompute automatic reminders</TooltipContent>
        </Tooltip>
      ) : null}
      <FiltersPopover
        filters={filters}
        onFilters={onFilters}
        teamOptions={teamOptions}
        extraFilters={extraFilters}
        extraFilterCount={extraFilterCount}
        onClearExtraFilters={onClearExtraFilters}
      />
      {viewControl === "segment" ? (
        <ViewSegment view={view} onView={onView} />
      ) : (
        <ViewMenu view={view} onView={onView} />
      )}
      <Button variant="primary" size="sm" onClick={onNew}>
        <Plus />
        New Event
      </Button>
    </div>
  </div>
);
